import React, { useCallback, useState } from 'react';
import {
  ContentSection,
  LeftSection,
  MainDiv,
  ModalBox,
  OwnerTag,
  Profile,
  SearchInput,
  SearchSection,
  UserEmail,
  UserInfo,
  UserName
} from './styles';
import { AVATAR_BORDER, EMPTY_STATE_SIZE, USER_ROLE } from '../../../global/constants';
import Avatar from '../../avatar';
import { rootStore } from '../../../mobx/rootStore';
import { addViewUser, removeViewUser } from '../../../services/viewServices';
import { isEmpty } from '../../../helpers/common';
import { ViewModel } from '../../../mobx/models/view';
import EmptyStateV2 from '../../emptyState';
import ModalHeader from '../modalHeader';
import CheckboxInput from '../../input/checkboxInput';

/**
 * ShareViewModal component for sharing views with team members
 * Shows owner tag for the current user and checkboxes for other members
 * @param {Function} onClose - Function to call when modal should be closed
 * @param {boolean} isOwner - Whether the current user is the owner of the view
 */
interface ShareViewModalProps {
  onClose: () => void;
  isOwner?: boolean;
  selectedView: ViewModel | undefined;
}

const ShareViewModal: React.FC<ShareViewModalProps> = ({ onClose, isOwner, selectedView }) => {
  const { workspaceStore } = rootStore;
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResult, setSearchResult] = useState([]);
  const [userIds, setUserIds] = useState<string[]>(selectedView?.userIds ?? []);

  const handleSearchValue = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setSearchQuery(value);
      const result =
        workspaceStore.currentWorkspace?.users?.filter(
          (item) =>
            item?.name?.toLowerCase().includes(value.toLowerCase()) ||
            item?.email?.toLowerCase().includes(value.toLowerCase())
        ) || [];
      setSearchResult(result as []);
    },
    [workspaceStore.currentWorkspace?.users]
  );

  const handleSelectedCheckbox = useCallback(
    async (userId: string) => {
      const setIds = userIds.includes(userId) ? userIds.filter((id) => id !== userId) : [...userIds, userId];
      setUserIds(setIds);

      if (userIds.includes(userId)) await removeViewUser({ users: [userId] }, selectedView?.id);
      else await addViewUser({ users: [userId] }, selectedView?.id);
    },
    [selectedView?.id, userIds]
  );

  return (
    <>
      <MainDiv>
        <ModalBox>
          <ModalHeader title='Share view' onClose={onClose} removeBottomBorder={true} />
          <SearchSection>
            <SearchInput
              placeholder='Search'
              autoFocus={true}
              value={searchQuery}
              onChange={(e) => handleSearchValue(e)}
            />
          </SearchSection>
        </ModalBox>
        <ContentSection>
          {!isEmpty(searchQuery) && searchResult.length === 0 && (
            <EmptyStateV2 description='No result found' size={EMPTY_STATE_SIZE.SMALL} isAlignedCenter={true} />
          )}
          {(!isEmpty(searchQuery) ? searchResult : workspaceStore.currentWorkspace?.users || [])
            .filter((user) => user.role !== USER_ROLE.CLIENT)
            .map((user) => (
              <Profile
                key={user.id}
                htmlFor={`shareView-${user.id}`}
                className={selectedView?.createdById === user.id ? 'owner-tag' : ''}>
                <LeftSection>
                  <Avatar
                    imgSrc={user.profile_photo || ''}
                    name={user.name || 'U N'}
                    size={28}
                    borderVariant={AVATAR_BORDER.NONE}
                  />
                  <UserInfo>
                    <UserName>{user.name}</UserName>
                    <UserEmail>{user.email}</UserEmail>
                  </UserInfo>
                </LeftSection>

                {selectedView?.createdById === user.id ? (
                  <OwnerTag>Owner</OwnerTag>
                ) : (
                  <CheckboxInput
                    checked={userIds?.includes(user.id) || false}
                    id={`shareView-${user.id}`}
                    onChange={() => handleSelectedCheckbox(user.id)}
                  />
                )}
              </Profile>
            ))}
        </ContentSection>
      </MainDiv>
    </>
  );
};

export default ShareViewModal;
